"use client";

import { useEffect, useState, useRef } from "react";
import Link from "next/link";
import { createClient } from "@/lib/supabase/client";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { BottomNav } from "@/components/bottom-nav";
import {
  ArrowLeft,
  Loader2,
  Star,
  MapPin,
  Clock,
  CheckCircle2,
  UserCheck,
  Briefcase,
  AlertCircle,
} from "lucide-react";
import type { Job } from "@/lib/types";

interface MatchWithWorker {
  id: string;
  job_id: string;
  worker_id: string;
  status: string;
  match_score: number | null;
  ai_summary: string | null;
  contractor_action: string;
  worker_action: string;
  hired_at: string | null;
  fee_charged: boolean;
  created_at: string;
  worker: {
    id: string;
    name: string | null;
    suburb: string | null;
    avatar_url: string | null;
  } | null;
  worker_profile: {
    trades: string[];
    experience_years: number | null;
    certifications: string[];
    availability: string | null;
  } | null;
}

interface SwipeableMatchCardProps {
  match: MatchWithWorker;
  isHired: boolean;
  justHired: boolean;
  hiring: boolean;
  onHire: (match: MatchWithWorker) => void;
  onReject: (match: MatchWithWorker) => void;
}

function SwipeableMatchCard({ match, isHired, justHired, hiring, onHire, onReject }: SwipeableMatchCardProps) {
  const [swipeX, setSwipeX] = useState(0);
  const [swiping, setSwiping] = useState(false);
  const startX = useRef(0);
  const currentX = useRef(0);

  const summary = match.ai_summary || generateAiSummary(match.worker, match.worker_profile);

  const handleTouchStart = (e: React.TouchEvent) => {
    if (isHired) return;
    startX.current = e.touches[0].clientX;
    setSwiping(true);
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    if (!swiping || isHired) return;
    currentX.current = e.touches[0].clientX;
    const diff = currentX.current - startX.current;
    // Allow both directions
    setSwipeX(Math.max(-150, Math.min(diff, 150)));
  };

  const handleTouchEnd = () => {
    if (!swiping || isHired) return;
    setSwiping(false);
    
    // If swiped right > 100px, hire
    if (swipeX > 100) {
      onHire(match);
    }
    // If swiped left > 100px, reject
    else if (swipeX < -100) {
      onReject(match);
    }
    
    // Reset
    setSwipeX(0);
  };

  const swipeProgress = Math.abs(swipeX) / 100;
  const swipeDirection = swipeX > 0 ? "right" : "left";

  return (
    <div className="relative">
      {/* Swipe indicator backgrounds */}
      {!isHired && swipeX !== 0 && (
        <>
          {/* Right swipe (hire) */}
          {swipeX > 0 && (
            <div 
              className="absolute inset-0 rounded-xl flex items-center justify-start pl-6 pointer-events-none"
              style={{ 
                backgroundColor: 'rgba(34, 197, 94, 0.1)',
                opacity: swipeProgress 
              }}
            >
              <UserCheck className="h-8 w-8 text-green-600" />
              <span className="ml-2 text-green-700 font-semibold">
                {swipeProgress >= 1 ? "Release to hire" : "Swipe to hire"}
              </span>
            </div>
          )}
          {/* Left swipe (reject) */}
          {swipeX < 0 && (
            <div 
              className="absolute inset-0 rounded-xl flex items-center justify-end pr-6 pointer-events-none"
              style={{ 
                backgroundColor: 'rgba(239, 68, 68, 0.1)',
                opacity: swipeProgress 
              }}
            >
              <span className="mr-2 text-red-700 font-semibold">
                {swipeProgress >= 1 ? "Release to pass" : "Swipe to pass"}
              </span>
              <AlertCircle className="h-8 w-8 text-red-600" />
            </div>
          )}
        </>
      )}
      
      <Card
        className={`transition-all touch-pan-y ${justHired ? "ring-2 ring-green-500" : ""}`}
        style={{
          transform: `translateX(${swipeX}px)`,
          transition: swiping ? 'none' : 'transform 0.2s ease-out',
        }}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
      >
        <CardContent className="p-5">
          <div className="flex items-start gap-3">
            {/* Avatar */}
            <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary font-bold text-xl">
              {match.worker?.name?.[0]?.toUpperCase() || "?"}
            </div>

            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2">
                <h3 className="font-semibold text-lg truncate">
                  {match.worker?.name || "Worker"}
                </h3>
                {isHired && (
                  <Badge className="bg-green-600 text-xs shrink-0">Hired</Badge>
                )}
              </div>

              {/* AI Summary */}
              <p className="mt-1 text-sm text-muted-foreground line-clamp-2">
                {summary}
              </p>

              {/* Details */}
              <div className="mt-3 flex flex-wrap gap-x-4 gap-y-2 text-sm text-muted-foreground">
                {match.worker_profile?.experience_years && (
                  <span className="flex items-center gap-1.5">
                    <Clock className="h-4 w-4" />
                    {match.worker_profile.experience_years}yr exp
                  </span>
                )}
                {match.worker?.suburb && (
                  <span className="flex items-center gap-1.5">
                    <MapPin className="h-4 w-4" />
                    {match.worker.suburb}
                  </span>
                )}
                {match.match_score && (
                  <span className="flex items-center gap-1.5">
                    <Star className="h-4 w-4 fill-amber-400 text-amber-400" />
                    {Math.round(match.match_score)}% match
                  </span>
                )}
              </div>

              {/* Certs */}
              {match.worker_profile?.certifications && match.worker_profile.certifications.length > 0 && (
                <div className="mt-3 flex flex-wrap gap-2">
                  {match.worker_profile.certifications.slice(0, 3).map((cert) => (
                    <Badge key={cert} variant="outline" className="text-xs px-2 py-1">
                      {cert}
                    </Badge>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Action */}
          {!isHired && (
            <Button
              onClick={() => onHire(match)}
              className="mt-4 w-full h-14 text-base font-semibold"
              disabled={hiring}
            >
              {hiring ? (
                <>
                  <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                  Processing...
                </>
              ) : (
                <>
                  <UserCheck className="mr-2 h-5 w-5" />
                  Hire — $50 Fee
                </>
              )}
            </Button>
          )}

          {justHired && (
            <div className="mt-4 flex items-center justify-center gap-2 rounded-lg bg-green-500/10 p-3 text-sm font-medium text-green-700">
              <CheckCircle2 className="h-5 w-5" />
              Hired! Worker has been notified.
            </div>
          )}

          {isHired && !justHired && (
            <div className="mt-4 flex items-center justify-center gap-2 text-sm text-muted-foreground">
              <CheckCircle2 className="h-5 w-5 text-green-600" />
              Hired on{" "}
              {match.hired_at
                ? new Date(match.hired_at).toLocaleDateString("en-AU", {
                    day: "numeric",
                    month: "short",
                  })
                : "recently"}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

const TRADE_LABEL: Record<string, string> = {
  "steel-fixer": "Steel Fixer",
  "formworker": "Formworker",
  "labourer": "Labourer",
  "concreter": "Concreter",
  "carpenter": "Carpenter",
  "scaffolder": "Scaffolder",
  "other": "Other",
};

function generateAiSummary(
  worker: MatchWithWorker["worker"],
  profile: MatchWithWorker["worker_profile"]
): string {
  const parts: string[] = [];
  if (profile?.trades?.length) {
    parts.push(profile.trades.map((t) => TRADE_LABEL[t] || t).join(", "));
  }
  if (profile?.experience_years) {
    parts.push(`${profile.experience_years} years exp`);
  }
  if (worker?.suburb) {
    parts.push(worker.suburb);
  }
  if (profile?.availability) {
    parts.push(profile.availability);
  }
  if (profile?.certifications?.length) {
    parts.push(profile.certifications.slice(0, 2).join(", "));
  }
  return parts.join(" \u00B7 ") || "Worker profile";
}

export default function ContractorMatchesPage() {
  const supabase = createClient();
  const [jobs, setJobs] = useState<Job[]>([]);
  const [selectedJobId, setSelectedJobId] = useState<string | null>(null);
  const [matches, setMatches] = useState<MatchWithWorker[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingMatches, setLoadingMatches] = useState(false);
  const [hiring, setHiring] = useState<string | null>(null);
  const [hireSuccess, setHireSuccess] = useState<string | null>(null);
  const [error, setError] = useState("");

  // Load contractor's jobs
  useEffect(() => {
    async function load() {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const { data: company } = await supabase
        .from("companies")
        .select("id")
        .eq("profile_id", user.id)
        .single();

      if (!company) {
        setLoading(false);
        return;
      }

      const { data: jobsData } = await supabase
        .from("jobs")
        .select("*")
        .eq("company_id", company.id)
        .in("status", ["active", "filled"])
        .order("created_at", { ascending: false });

      if (jobsData && jobsData.length > 0) {
        setJobs(jobsData);
        setSelectedJobId(jobsData[0].id);
      }
      setLoading(false);
    }
    load();
  }, [supabase]);

  // Load matches for selected job
  useEffect(() => {
    if (!selectedJobId) return;
    async function loadMatches() {
      setLoadingMatches(true);

      // Get matches for this job
      const { data: matchData } = await supabase
        .from("matches")
        .select("*")
        .eq("job_id", selectedJobId)
        .order("match_score", { ascending: false });

      if (!matchData || matchData.length === 0) {
        setMatches([]);
        setLoadingMatches(false);
        return;
      }

      // Fetch worker profiles and base profiles
      const workerIds = matchData.map((m: { worker_id: string }) => m.worker_id);

      const [{ data: profiles }, { data: workerProfiles }] = await Promise.all([
        supabase.from("profiles").select("id, name, suburb, avatar_url").in("id", workerIds),
        supabase.from("worker_profiles").select("profile_id, trades, experience_years, certifications, availability").in("profile_id", workerIds),
      ]);

      type ProfileRow = { id: string; name: string | null; suburb: string | null; avatar_url: string | null };
      type WPRow = { profile_id: string; trades: string[]; experience_years: number | null; certifications: string[]; availability: string | null };

      const profileMap = new Map((profiles || []).map((p: ProfileRow) => [p.id, p]));
      const wpMap = new Map((workerProfiles || []).map((w: WPRow) => [w.profile_id, w]));

      const enriched: MatchWithWorker[] = matchData.map((m: Record<string, unknown>) => ({
        id: m.id as string,
        job_id: m.job_id as string,
        worker_id: m.worker_id as string,
        status: m.status as string,
        match_score: m.match_score as number | null,
        ai_summary: m.ai_summary as string | null,
        contractor_action: m.contractor_action as string,
        worker_action: m.worker_action as string,
        hired_at: m.hired_at as string | null,
        fee_charged: m.fee_charged as boolean,
        created_at: m.created_at as string,
        worker: profileMap.get(m.worker_id as string) || null,
        worker_profile: wpMap.get(m.worker_id as string) || null,
      }));

      setMatches(enriched);
      setLoadingMatches(false);
    }
    loadMatches();
  }, [selectedJobId, supabase]);

  async function handleHire(match: MatchWithWorker) {
    setHiring(match.id);
    setError("");

    // Haptic feedback
    if (navigator.vibrate) {
      navigator.vibrate(50);
    }

    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      // Get company for payment
      const { data: company } = await supabase
        .from("companies")
        .select("id")
        .eq("profile_id", user.id)
        .single();

      if (!company) {
        setError("Company not found.");
        setHiring(null);
        return;
      }

      // Update match to hired
      const { error: matchError } = await supabase
        .from("matches")
        .update({
          status: "hired",
          contractor_action: "accepted",
          hired_at: new Date().toISOString(),
          fee_charged: true,
        })
        .eq("id", match.id);

      if (matchError) {
        setError(matchError.message);
        setHiring(null);
        return;
      }

      // Create payment record
      await supabase.from("payments").insert({
        match_id: match.id,
        company_id: company.id,
        amount: 50.0,
        status: "pending",
      });

      // Update local state
      setMatches((prev) =>
        prev.map((m) =>
          m.id === match.id
            ? { ...m, status: "hired", contractor_action: "accepted", hired_at: new Date().toISOString(), fee_charged: true }
            : m
        )
      );

      setHireSuccess(match.id);
      setTimeout(() => setHireSuccess(null), 3000);
    } catch {
      setError("Something went wrong. Please try again.");
    }

    setHiring(null);
  }

  async function handleReject(match: MatchWithWorker) {
    // Haptic feedback
    if (navigator.vibrate) {
      navigator.vibrate([30, 20, 30]);
    }

    // Remove from list (soft delete - just update status)
    await supabase
      .from("matches")
      .update({
        contractor_action: "rejected",
      })
      .eq("id", match.id);

    // Remove from UI
    setMatches((prev) => prev.filter((m) => m.id !== match.id));
  }

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background pb-20">
      {/* Header */}
      <header className="sticky top-0 z-50 border-b bg-background/95 backdrop-blur">
        <div className="mx-auto flex h-16 max-w-screen-sm items-center px-4">
          <Link
            href="/dashboard"
            className="mr-3 flex h-12 w-12 items-center justify-center rounded-full active:bg-muted"
          >
            <ArrowLeft className="h-6 w-6" />
          </Link>
          <h1 className="text-xl font-bold">Matches</h1>
        </div>
      </header>

      <main className="mx-auto max-w-screen-sm px-4 py-4">
        {jobs.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-20 text-center px-4">
            <Briefcase className="h-16 w-16 text-muted-foreground/50" />
            <p className="mt-4 text-xl font-bold">No jobs posted yet</p>
            <p className="mt-2 text-base text-muted-foreground">
              Post a job to start seeing matched workers.
            </p>
            <Button asChild className="mt-8 h-16 px-8 text-lg font-semibold">
              <Link href="/contractor/post-job">Post a Job</Link>
            </Button>
          </div>
        ) : (
          <>
            {/* Job selector — horizontal scroll */}
            {jobs.length > 1 && (
              <div className="flex gap-2 overflow-x-auto pb-3 -mx-4 px-4 scrollbar-hide">
                {jobs.map((job) => (
                  <button
                    key={job.id}
                    onClick={() => setSelectedJobId(job.id)}
                    className={`shrink-0 rounded-full px-5 py-3 text-base font-semibold transition-all active:scale-95 min-h-[48px] ${
                      selectedJobId === job.id
                        ? "bg-primary text-primary-foreground"
                        : "bg-muted text-muted-foreground"
                    }`}
                  >
                    {job.title}
                  </button>
                ))}
              </div>
            )}

            {/* Selected job summary */}
            {selectedJobId && (
              <div className="mb-4 rounded-xl bg-muted/50 p-3">
                {(() => {
                  const job = jobs.find((j) => j.id === selectedJobId);
                  if (!job) return null;
                  return (
                    <div className="flex items-center gap-3 text-sm">
                      <Badge variant="secondary">{TRADE_LABEL[job.trade] || job.trade}</Badge>
                      {job.suburb && (
                        <span className="flex items-center gap-1 text-muted-foreground">
                          <MapPin className="h-3.5 w-3.5" />
                          {job.suburb}
                        </span>
                      )}
                      <span className="text-muted-foreground">
                        {matches.length} match{matches.length !== 1 ? "es" : ""}
                      </span>
                    </div>
                  );
                })()}
              </div>
            )}

            {/* Matches */}
            {loadingMatches ? (
              <div className="flex justify-center py-12">
                <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
              </div>
            ) : matches.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-16 text-center">
                <UserCheck className="h-12 w-12 text-muted-foreground/50" />
                <p className="mt-4 text-lg font-medium">No matches yet</p>
                <p className="mt-1 text-sm text-muted-foreground">
                  Workers will show up here as they apply. We&apos;ll notify you.
                </p>
              </div>
            ) : (
              <div className="space-y-4">
                {error && (
                  <div className="flex items-center gap-2 rounded-lg bg-destructive/10 p-3 text-sm text-destructive">
                    <AlertCircle className="h-4 w-4 shrink-0" />
                    {error}
                  </div>
                )}

                {/* Swipe hint */}
                {matches.length > 0 && matches.some(m => m.status !== "hired") && (
                  <div className="text-center">
                    <p className="text-sm text-muted-foreground">
                      👉 Swipe right to hire • Swipe left to pass
                    </p>
                  </div>
                )}

                {matches.map((match) => (
                  <SwipeableMatchCard
                    key={match.id}
                    match={match}
                    isHired={match.status === "hired"}
                    justHired={hireSuccess === match.id}
                    hiring={hiring === match.id}
                    onHire={handleHire}
                    onReject={handleReject}
                  />
                ))}
              </div>
            )}
          </>
        )}
      </main>

      <BottomNav userType="contractor" />
    </div>
  );
}
